"use client"

import React from "react"

import { useState } from "react"
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useInView } from "@/hooks/use-in-view"
import { cn } from "@/lib/utils"

export function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const { ref: titleRef, isInView: titleInView } =
    useInView<HTMLHeadingElement>()

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)

    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1500))

    setIsSubmitting(false)
    setSubmitted(true)
  }

  return (
    <section id="contacto" className="py-16 lg:py-24 bg-background-contact">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div>
            <h2
              ref={titleRef}
              className={cn(
                "mt-4 font-work text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-titulo-hero transition-all duration-1000 ease-out will-change-transform",
                "motion-reduce:transition-none motion-reduce:transform-none",
                titleInView
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-3",
              )}
            >
              Hablemos de tu proyecto
            </h2>
            <p
              className={cn(
                "mt-6 text-lg font-jetbrains font-extralight text-titulo-hero/60 leading-relaxed transition-all duration-1000 ease-out will-change-transform",
                "motion-reduce:transition-none motion-reduce:transform-none",
                titleInView
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-3",
              )}
            >
              Cuéntanos sobre los procesos que te gustaría automatizar. Nuestro equipo 
              analizará tu caso y te contactará en menos de 24 horas con una propuesta inicial.
            </p>

            <div className="mt-12 space-y-6">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-secondary/50 text-titulo-hero/70">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-jetbrains font-extralight text-titulo-hero/60">Email</div>
                  <a
                    href="mailto:hola@hercode.io"
                    className="text-titulo-hero hover:text-titulo-red transition-colors"
                  >
                    info@hercode.cc
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-secondary/50 text-titulo-hero/70">
                  <Phone className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-jetbrains font-extralight text-titulo-hero/60">Teléfonos</div>
                  <a
                    href="tel:+543517576643"
                    className="text-titulo-hero hover:text-titulo-red transition-colors"
                  >
                     +54 3517576643<br></br>  
                  </a>
                  <a
                    href="tel:+5433704340351"
                    className="text-titulo-hero hover:text-titulo-red transition-colors"
                  >
                    +54 3704340351
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-secondary/50 text-titulo-hero/70">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-jetbrains font-extralight text-titulo-hero/60">Ubicación</div>
                  <span className="text-titulo-hero">
                    Remoto Global
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="rounded-2xl p-8">
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary/50 text-titulo-hero/70 mb-6">
                  <Send className="h-8 w-8" />
                </div>
                <h3 className="font-work text-2xl font-semibold text-titulo-hero">
                  ¡Mensaje enviado!
                </h3>
                <p className="mt-3 font-jetbrains font-extralight text-titulo-hero/60 max-w-sm">
                  Gracias por contactarnos. Nuestro equipo revisará tu solicitud y te responderá en menos de 24 horas.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-sm font-jetbrains font-extralight text-titulo-hero/70">Nombre</label>
                    <input
                      id="name"
                      name="name"
                      placeholder="Tu nombre"
                      required
                      className="h-10 w-full rounded-md border border-titulo-hero/20 bg-secondary/50  px-3 text-sm text-titulo-hero/80 placeholder:text-titulo-hero/40 focus:outline-none focus:ring-1 focus:ring-ring/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="company" className="text-sm font-jetbrains font-extralight text-titulo-hero/70">Empresa</label>
                    <input
                      id="company"
                      name="company"
                      placeholder="Nombre de tu empresa"
                      className="h-10 w-full rounded-md border border-titulo-hero/20 bg-secondary/50 px-3 text-sm text-titulo-hero/80 placeholder:text-titulo-hero/40 focus:outline-none focus:ring-1 focus:ring-ring/50"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-jetbrains font-extralight text-titulo-hero/70">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="tu@email.com"
                    required
                    className="h-10 w-full rounded-md border border-titulo-hero/20 bg-secondary/50 px-3 text-sm text-titulo-hero/80 placeholder:text-titulo-hero/40 focus:outline-none focus:ring-1 focus:ring-ring/50"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="phone" className="text-sm font-jetbrains font-extralight text-titulo-hero/70">Teléfono (opcional)</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+54 123456789"
                    className="h-10 w-full rounded-md border border-titulo-hero/20 bg-secondary/50 px-3 text-sm text-titulo-hero/80 placeholder:text-titulo-hero/40 focus:outline-none focus:ring-1 focus:ring-ring/50"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="message" className="text-sm font-jetbrains font-extralight text-titulo-hero/70">Cuéntanos sobre tu proyecto</label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Comentanos en que podemos ayudarte..."
                    rows={4}
                    required
                    className="w-full rounded-md border border-titulo-hero/20 bg-secondary/50 px-3 py-2 text-sm text-titulo-hero/80 placeholder:text-titulo-hero/40 focus:outline-none focus:ring-1 focus:ring-ring/50 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      Enviar mensaje
                      <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
